import React, { useState } from "react";
import { Button, ButtonProps } from "~/components/ui/button";
import * as Analytics from "~/modules/billing/analytics";

// // // //

export function CheckoutForm(props: {
    className?: string;
    text?: string;
    size?: "sm" | "lg";
    variant?: ButtonProps["variant"];
    /** Optional icon rendered next to the button text */
    icon?: React.ReactNode;
    /** Which side of the text the icon sits on (default "start") */
    iconPosition?: "start" | "end";
    organizationId?: string;
    enableAdditionalSeatsSelect?: boolean;
    monthly50?: boolean;
    academic?: boolean;
    cancelRedirect?: string;
    /**
     * When true, the checkout starts a 7-day free trial. The server only honors
     * this for trial-eligible organizations (currently on FREE, never a Stripe
     * customer); otherwise it falls back to a normal checkout.
     */
    startTrial?: boolean;
}) {
    // Academic checkouts are single-seat only, so the seats select is never shown.
    const showAdditionalSeats = props.academic
        ? false
        : (props.enableAdditionalSeatsSelect ?? true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        Analytics.trackClickCheckoutButton({
            organizationId: props.organizationId,
            startTrial: props.startTrial,
            academic: props.academic,
            monthly50: props.monthly50,
        });

        // Set submitting state immediately
        // Don't disable the select field so its value is included in submission
        setIsSubmitting(true);
    };

    return (
        <form
            action="/api/payments/checkout_sessions"
            method="POST"
            encType="application/x-www-form-urlencoded"
            onSubmit={handleSubmit}
        >
            {props.organizationId && (
                <input
                    type="hidden"
                    name="organization_id"
                    value={props.organizationId}
                />
            )}
            {props.monthly50 && (
                <input
                    type="hidden"
                    name="product"
                    value="downloads_50_monthly"
                />
            )}
            {props.academic && (
                <input type="hidden" name="product" value="academic_monthly" />
            )}
            {props.cancelRedirect && (
                <input
                    type="hidden"
                    name="cancel_redirect"
                    value={props.cancelRedirect}
                />
            )}
            {props.startTrial && !props.academic && (
                <input type="hidden" name="start_trial" value="true" />
            )}
            <div className={showAdditionalSeats ? "space-y-4" : ""}>
                {showAdditionalSeats && (
                    <div className="flex flex-col gap-2">
                        <label
                            htmlFor="license_count"
                            className="text-sm font-medium text-muted-foreground"
                        >
                            Seats
                        </label>
                        <select
                            id="license_count"
                            name="license_count"
                            defaultValue="1"
                            className="w-full rounded-md border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                                <option key={num} value={num}>
                                    {num} {num === 1 ? "seat" : "seats"}
                                </option>
                            ))}
                            <option value={10}>10+ seats</option>
                        </select>
                    </div>
                )}
                <Button
                    type="submit"
                    size={props.size}
                    variant={props.variant}
                    className={props.className}
                    disabled={isSubmitting}
                >
                    {!isSubmitting &&
                        props.iconPosition !== "end" &&
                        props.icon}
                    {isSubmitting ? "Processing..." : props.text || "Checkout"}
                    {!isSubmitting &&
                        props.iconPosition === "end" &&
                        props.icon}
                </Button>
            </div>
        </form>
    );
}
